import Decimal from 'decimal.js';
import { useHistory } from 'react-router-dom';
import { useStore } from 'components/Store';
import { parseServerError } from 'helpers/errorHelpers';
import { Maths } from 'helpers/maths';
import useCropSeasons from 'hooks/useCropSeasons';
import useInventoryApi from 'hooks/useInventoryApi';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { paths } from 'routes/paths';
import usePlanData from 'screens/Plans/Plan/hooks/usePlanData';
import { findCropSeasonIdForCropZone } from 'screens/Plans/Plan/helpers/getSelectedCropSeasonIds';
import planNotification from '../components/Notification';
import { printPlanViaBridge } from '../components/Notification/planPrintBridge';
import {
  PlanWizardFormValues,
  PlanProductLineItem,
  PlanCropZone
} from '../types';
import { FetchedPlan } from '../helpers/planFormMapping';

export type PlanWizardMode = 'create' | 'edit' | 'duplicate';

/**
 * Prefers a prefilled zone's own `cropSeasonId` (Edit/Duplicate) over
 * `findCropSeasonIdForCropZone`'s lookup, which only matches currently
 * breadcrumb-selected seasons.
 */
const resolveCropSeasonId = (
  zone: PlanCropZone,
  cropSeasons: any[],
  selectedCropSeasonIds: string[]
): string | undefined =>
  zone.cropSeasonId ??
  findCropSeasonIdForCropZone(zone.id, cropSeasons, selectedCropSeasonIds)[0]
    ?.id;

/**
 * Mirrors `Plan/index.jsx`'s `cleanSaveMetaData` — gated on whether a crop
 * zone actually *matched* a selected crop season, not merely on whether any
 * crop zones exist, or an unmatched zone would send an empty
 * `cropSeasonIds: []` instead of falling back to `selectedCropSeasonIds`.
 */
const buildCropSeasonIds = (
  values: PlanWizardFormValues,
  cropSeasons: any[],
  selectedCropSeasonIds: string[]
) => {
  const matchedCropSeasonIds = [
    ...new Set(
      values.cropZones.flatMap(zone => {
        const cropSeasonId = resolveCropSeasonId(
          zone,
          cropSeasons,
          selectedCropSeasonIds
        );
        return cropSeasonId ? [cropSeasonId] : [];
      })
    )
  ];

  return matchedCropSeasonIds.length > 0
    ? matchedCropSeasonIds
    : selectedCropSeasonIds;
};

/**
 * Mirrors `Plan/index.jsx`'s `newCropzonesMapping` — each zone must carry its
 * own resolved `cropSeasonId`, or the backend rejects the save ("crop
 * seasons without any selected crop zones").
 */
const buildCropZonesPayload = (
  values: PlanWizardFormValues,
  cropSeasons: any[],
  selectedCropSeasonIds: string[]
) =>
  values.cropZones.map(zone => ({
    // Only Edit's prefilled zones carry `associationId` (see `PlanCropZone`)
    // — omitting the key for create/duplicate rather than sending `id: ''`
    // matches `cropId`'s same "absent, not empty" convention above.
    ...(zone.associationId ? { id: zone.associationId } : {}),
    cropZoneIdValue: zone.id,
    cropSeasonId:
      resolveCropSeasonId(zone, cropSeasons, selectedCropSeasonIds) ?? '',
    areaValue: zone.appliedAcreage ?? zone.area ?? 0,
    areaUnit: 'acre',
    coveragePct: Maths.divideSafeRound(zone.coveragePercent ?? 100, 100)
  }));

/**
 * Converts `coveragePercent` from Formik's 0-100 scale to the backend's 0-1
 * fraction, and strips client-only fields the backend's product schema rejects.
 */
const buildPlanProductEntriesPayload = (
  entries: PlanWizardFormValues['planProductEntries']
): Array<Omit<
  PlanProductLineItem,
  | 'stdpackageunit'
  | 'stdunit'
  | 'stdfactor'
  | 'density'
  | 'productName'
  | 'shareOwnerInformation'
  | 'producttype'
  | 'registrationnumber'
  | 'saveAsNewListPrice'
>> =>
  entries.map(entry => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      stdpackageunit,
      stdunit,
      stdfactor,
      density,
      productName,
      shareOwnerInformation,
      producttype,
      registrationnumber,
      saveAsNewListPrice,
      ...rest
    } = entry;
    return {
      ...rest,
      coveragePercent: Maths.parseFloatRound(
        Maths.divide(entry.coveragePercent, 100).toNumber(),
        4,
        Decimal.ROUND_HALF_UP
      ),
      // Backend's "no target date" is epoch 0, not ''.
      targetDateEpoch: entry.targetDateEpoch || 0
    };
  });

/**
 * Mirrors Applications/Work Orders' `AgOperationUtilityProvider.handleListPrice`
 * request shape rather than importing that provider directly, since Plans
 * has no use for its broader weather/step orchestration.
 */
const buildListPricePayload = (
  entries: PlanWizardFormValues['planProductEntries']
) =>
  entries
    .filter(entry => entry.saveAsNewListPrice)
    .map(entry => ({
      productId: entry.productId,
      averagePricePerUnit: entry.specificCostValue,
      averagePricePerUnitUnit: entry.specificCostUnit,
      productDensity: entry.density
    }));

const buildFixedCostAndRevenuePayload = (values: PlanWizardFormValues) => ({
  fixedCost: {
    landRent: values.fixedCost.landRent_PerAc || 0,
    equipment: values.fixedCost.equipment_PerAc || 0,
    insurance: values.fixedCost.insurance_PerAc || 0,
    labor: values.fixedCost.labor_PerAc || 0,
    repairs: values.fixedCost.repairs_PerAc || 0,
    taxes: values.fixedCost.taxes_PerAc || 0,
    returnToMgt: values.fixedCost.returnToMgt_PerAc || 0
  },
  projectedRevenue: {
    yieldPerArea: values.projectedRevenue.yieldPerArea || 0,
    fsaPayment: values.projectedRevenue.fsaPayment || 0,
    cropSharePercent: Maths.divideSafeRound(
      values.projectedRevenue.cropSharePercent ?? 100,
      100
    ),
    pricePerUnit: values.projectedRevenue.pricePerUnit || 0
  }
});

/**
 * Builds the create payload from the wizard's flat Formik values, mirroring
 * `Plan/index.jsx`'s `cleanSaveMetaData` (create-mode path only). A
 * near-duplicate rather than a shared import: `cleanSaveMetaData` is a
 * closure over that component's state, not an exported pure function.
 */
export const buildPlanCreatePayload = (
  values: PlanWizardFormValues,
  cropSeasons: any[],
  selectedCropSeasonIds: string[],
  currency = 'USD'
) => ({
  name: values.name || '',
  estimatedAreaUnit: values.estimatedAreaUnit || 'acre',
  estimatedAreaValue: values.estimatedAreaValue || 1,
  ...buildFixedCostAndRevenuePayload(values),
  // Matches `Plan/index.jsx`'s `cleanSaveMetaData`, which strips `notes`
  // from the save payload entirely when empty rather than sending an empty
  // string — spread in only when the Notes field actually has a value.
  ...(values.notes ? { notes: values.notes } : {}),
  cropSeasonIds: buildCropSeasonIds(values, cropSeasons, selectedCropSeasonIds),
  // `tagIds` is the wizard's Formik field name (matching the reused
  // `TagsSection`'s hardcoded field), but the Plan entity's actual field is
  // `flagIds` — same underlying `tags`/`flags` resource the legacy
  // `PlanView`/`PlanTable` already read/write (PR7, FR-020).
  flagIds: values.tagIds || [],
  planProductEntries: buildPlanProductEntriesPayload(values.planProductEntries),
  cropZones: buildCropZonesPayload(values, cropSeasons, selectedCropSeasonIds),
  // Omitting the key entirely when empty (rather than sending `cropId: ''`)
  // keeps the backend's optional-but-must-be-a-UUID validation from
  // rejecting the save with "cropId must be a UUID".
  cropId: values.cropId || undefined,
  currency
});

/**
 * Builds the update payload for editing an existing Plan (PR5, FR-017) —
 * adds `id`/`organizationId`, which the create path omits. `currency` is
 * the source Plan's own currency; omit it only for a genuinely new Plan.
 */
export const buildPlanUpdatePayload = (
  values: PlanWizardFormValues,
  cropSeasons: any[],
  selectedCropSeasonIds: string[],
  planId: string,
  organizationId?: string,
  currency?: string
) => ({
  id: planId,
  organizationId,
  ...buildPlanCreatePayload(
    values,
    cropSeasons,
    selectedCropSeasonIds,
    currency
  )
});

export interface UsePlanWizardMutationsReturn {
  plan: FetchedPlan;
  loading: boolean;
  saving: boolean;
  submitPlan: (values: PlanWizardFormValues) => Promise<unknown>;
}

/**
 * Single data hook for the Plan wizard's create/edit/duplicate flows —
 * centralizes the fetch so it happens exactly once per route.
 */
const usePlanWizardMutations = (
  mode: PlanWizardMode,
  planId?: string
): UsePlanWizardMutationsReturn => {
  const dataId = mode === 'create' ? 'plan' : planId || 'plan';
  const { plan, loading, saving, savePlan } = usePlanData(dataId) as {
    plan: FetchedPlan;
    loading: boolean;
    saving: boolean;
    savePlan: (values: unknown, duplicating?: string) => Promise<unknown>;
  };
  const { cropSeasons } = useCropSeasons();
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const inventoryAPI = useInventoryApi('inventory/products-prices');
  const { dispatch } = useStore();
  const history = useHistory();

  const persistListPrices = (values: PlanWizardFormValues) => {
    const productsWithSavedListPrice = buildListPricePayload(
      values.planProductEntries
    );
    if (productsWithSavedListPrice.length === 0) {
      return Promise.resolve();
    }
    // Mirrors `handleListPrice`'s own resilience: a list-price save failure
    // is reported but never rejects the caller's promise chain, so it can't
    // undo an otherwise-successful Plan save.
    return (inventoryAPI as any)
      .post(productsWithSavedListPrice)
      .promise.catch(parseServerError(dispatch));
  };

  // `savePlan`'s promise resolves to the axios response on success and to
  // `undefined` after `usePlanData` swallows a failed save into a global
  // error banner, so `result` alone distinguishes the two cases.
  const showSaveNotification = (
    values: PlanWizardFormValues,
    result: unknown
  ) => {
    if (!result) {
      planNotification('CREATE_OR_EDIT_ERROR');
      return;
    }
    if (mode === 'edit') {
      planNotification('EDIT_SUCCESS');
      return;
    }
    const savedPlanId = (result as { data?: { id?: string } })?.data?.id;
    planNotification('CREATE_SUCCESS', undefined, {
      onDuplicate: savedPlanId
        ? () =>
            history.push(`${paths.plans}/create`, {
              flow: 'duplicate',
              sourcePlanId: savedPlanId
            })
        : undefined,
      onPrint: savedPlanId ? () => printPlanViaBridge(savedPlanId) : undefined
    });
  };

  const saveThenPersistListPrices = (
    savedPlan: Promise<unknown>,
    values: PlanWizardFormValues
  ) =>
    savedPlan.then(result =>
      persistListPrices(values).then(() => {
        showSaveNotification(values, result);
        return result;
      })
    );

  const submitPlan = (values: PlanWizardFormValues) => {
    if (mode === 'edit') {
      const payload = buildPlanUpdatePayload(
        values,
        cropSeasons ?? [],
        selectedCropSeasonIds || [],
        dataId,
        plan?.organizationId,
        plan?.currency
      );
      return saveThenPersistListPrices(savePlan(payload), values);
    }
    if (mode === 'duplicate') {
      const payload = buildPlanCreatePayload(
        values,
        cropSeasons ?? [],
        selectedCropSeasonIds || [],
        plan?.currency
      );
      // Forces the create path in `usePlanData.savePlan` despite `dataId`
      // being the source Plan's real id — matches legacy `duplicate()`'s
      // `selectedView: 'edit-dup'` route state exactly.
      return saveThenPersistListPrices(savePlan(payload, 'edit-dup'), values);
    }
    const payload = buildPlanCreatePayload(
      values,
      cropSeasons ?? [],
      selectedCropSeasonIds || []
    );
    return saveThenPersistListPrices(savePlan(payload), values);
  };

  return { plan, loading, saving, submitPlan };
};

export default usePlanWizardMutations;
